import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import {
  Search,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  Stethoscope,
  X,
  DollarSign,
  Building2,
  CreditCard,
  Lock,
} from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useAppReviewFlags } from '@/config/appReviewFlags';
import { scenarioStorage } from '@/lib/scenarioStorage';
import { createCarePlanCheckout } from '@/services/stripe';

interface WizardScenario {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  total_procedures: number | null;
  estimated_cost: number | null;
}

interface CreateCarePlanWizardProps {
  onClose: () => void;
  preselectedScenarioIds?: string[];
  familyMemberName?: string;
}

export function CreateCarePlanWizard({
  onClose,
  preselectedScenarioIds,
  familyMemberName,
}: CreateCarePlanWizardProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const navigate = useNavigate();
  // App-review gate: Stripe checkout is hidden on the native app until an
  // approved payment path exists. Always true on web.
  const { showPayments } = useAppReviewFlags();

  const [step, setStep] = useState(1);
  const [selectedScenarios, setSelectedScenarios] = useState<WizardScenario[]>([]);
  const [scenarioSearch, setScenarioSearch] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [location, setLocation] = useState<{ lat: number; lon: number; label: string; state: string } | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState(50);
  const [consolidationLevel, setConsolidationLevel] = useState(50);
  const [planName, setPlanName] = useState('');
  const [insuranceStatus, setInsuranceStatus] = useState('uninsured');
  const [email, setEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available scenarios from DB
  const { data: dbScenarios = [], isLoading: loadingScenarios } = useQuery({
    queryKey: ['wizard-scenarios'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scenarios')
        .select('id, name, description, category, total_procedures, estimated_cost')
        .eq('is_active', true)
        .eq('hidden_from_catalog', false)
        .order('name');
      if (error) throw error;
      return data as WizardScenario[];
    },
  });

  // Pre-select scenarios if provided
  useEffect(() => {
    if (preselectedScenarioIds && dbScenarios.length > 0) {
      const preselected = dbScenarios.filter(s => preselectedScenarioIds.includes(s.id));
      if (preselected.length > 0) setSelectedScenarios(preselected);
    }
  }, [preselectedScenarioIds, dbScenarios]);

  // Try to get GPS location on mount
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const resp = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`
            );
            const data = await resp.json();
            const city = data.address?.city || data.address?.town || data.address?.village || '';
            const state = data.address?.state || '';
            const stateCode = data.address?.['ISO3166-2-lvl4']?.split('-')[1] || '';
            if (city) {
              setLocation({
                lat: pos.coords.latitude,
                lon: pos.coords.longitude,
                label: `${city}, ${stateCode || state}`,
                state: stateCode,
              });
              setLocationInput(`${city}, ${stateCode || state}`);
            }
          } catch { /* ignore geocode failure */ }
        },
        () => { /* permission denied or error — user can type manually */ }
      );
    }
  }, []);

  const filteredScenarios = dbScenarios.filter(s =>
    !scenarioSearch || s.name.toLowerCase().includes(scenarioSearch.toLowerCase()) ||
    (s.category && s.category.toLowerCase().includes(scenarioSearch.toLowerCase()))
  );

  const toggleScenario = (scenario: WizardScenario) => {
    setSelectedScenarios(prev => {
      const exists = prev.find(s => s.id === scenario.id);
      return exists ? prev.filter(s => s.id !== scenario.id) : [...prev, scenario];
    });
  };

  const handleGeocode = async () => {
    if (!locationInput.trim()) return;
    setGeocoding(true);
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationInput)}&format=json&limit=1&countrycodes=us`
      );
      const data = await resp.json();
      if (data && data.length > 0) {
        const result = data[0];
        // Extract state from display_name
        const parts = result.display_name.split(', ');
        const stateStr = parts.length >= 3 ? parts[parts.length - 2] : '';
        setLocation({
          lat: parseFloat(result.lat),
          lon: parseFloat(result.lon),
          label: locationInput.trim(),
          state: stateStr.length === 2 ? stateStr : '',
        });
      } else {
        setError('Could not find that location. Try "City, ST" format.');
      }
    } catch {
      setError('Geocoding failed. Check your connection.');
    }
    setGeocoding(false);
  };

  const handleCreate = async () => {
    if (!showPayments) return;
    if (selectedScenarios.length === 0 || !location) return;
    setCreating(true);
    setError(null);

    try {
      // Store plan config in localStorage for the checkout success page
      const planConfig = {
        anonymous_user_id: scenarioStorage.getUserId(),
        plan_name: planName.trim() || `Care Plan - ${location.label}`,
        latitude: location.lat,
        longitude: location.lon,
        location_label: location.label,
        location_state: location.state,
        search_radius_miles: radiusMiles,
        insurance_status: insuranceStatus,
        consolidation_level: consolidationLevel,
        user_email: email || null,
        family_member_name: familyMemberName || null,
        scenario_ids: selectedScenarios.map(s => s.id),
      };
      localStorage.setItem('pending_care_plan_config', JSON.stringify(planConfig));

      // Create Stripe checkout session and redirect
      const origin = window.location.origin;
      const checkoutUrl = await createCarePlanCheckout({
        successUrl: `${origin}/plan/checkout-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/family`,
      });

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to start checkout');
      setCreating(false);
    }
  };

  const totalSteps = 4;
  const canNext = step === 1
    ? selectedScenarios.length > 0
    : step === 2
    ? !!location
    : true;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
      isAhp ? 'bg-black/40' : 'bg-black/60'
    }`}>
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${
        isAhp ? 'bg-white' : 'bg-card border border-foreground/10'
      }`}>
        {/* Header */}
        <div className={`p-4 flex items-center justify-between border-b ${
          isAhp ? 'border-gray-100 bg-gray-50' : 'border-foreground/10'
        }`}>
          <div>
            <h2 className={`text-lg font-semibold ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
              Create Care Plan
            </h2>
            <p className={`text-xs ${isAhp ? 'text-gray-500' : 'text-muted-foreground'}`}>
              Step {step} of {totalSteps}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-foreground/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className={`h-1 ${isAhp ? 'bg-gray-100' : 'bg-foreground/10'}`}>
          <div
            className={`h-full transition-all ${isAhp ? 'bg-[#002868]' : 'bg-matrix-green'}`}
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {/* Step 1: Select Scenarios */}
          {step === 1 && (
            <div className="space-y-3">
              <p className={`text-sm ${isAhp ? 'text-gray-600' : 'text-muted-foreground'}`}>
                Select the medical scenarios you need care for:
              </p>

              <div className="relative">
                <Search className={`absolute left-3 top-2.5 w-4 h-4 ${
                  isAhp ? 'text-gray-400' : 'text-muted-foreground'
                }`} />
                <Input
                  placeholder="Search scenarios..."
                  value={scenarioSearch}
                  onChange={e => setScenarioSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Selected chips */}
              {selectedScenarios.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedScenarios.map(s => (
                    <Badge
                      key={s.id}
                      variant="secondary"
                      className="cursor-pointer text-xs"
                      onClick={() => toggleScenario(s)}
                    >
                      {s.name}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}

              <div className="space-y-1 max-h-[35vh] overflow-y-auto">
                {loadingScenarios ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : (
                  filteredScenarios.map(s => {
                    const isSelected = selectedScenarios.some(sel => sel.id === s.id);
                    return (
                      <button
                        key={s.id}
                        onClick={() => toggleScenario(s)}
                        className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition ${
                          isSelected
                            ? isAhp ? 'bg-blue-50 border border-blue-200' : 'bg-matrix-green/10 border border-matrix-green/30'
                            : isAhp ? 'hover:bg-gray-50 border border-transparent' : 'hover:bg-foreground/5 border border-transparent'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                          isSelected
                            ? isAhp ? 'bg-[#002868] border-[#002868]' : 'bg-matrix-green border-matrix-green'
                            : isAhp ? 'border-gray-300' : 'border-foreground/30'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
                            {s.name}
                          </p>
                          {s.category && (
                            <p className={`text-xs ${isAhp ? 'text-gray-400' : 'text-muted-foreground'}`}>
                              {s.category}
                            </p>
                          )}
                        </div>
                        {s.total_procedures && (
                          <span className={`text-xs ${isAhp ? 'text-gray-400' : 'text-muted-foreground'}`}>
                            {s.total_procedures} proc
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <div className="space-y-4">
              <p className={`text-sm ${isAhp ? 'text-gray-600' : 'text-muted-foreground'}`}>
                Where are you located? We'll find the cheapest hospitals nearby.
              </p>

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <MapPin className={`absolute left-3 top-2.5 w-4 h-4 ${
                    isAhp ? 'text-gray-400' : 'text-muted-foreground'
                  }`} />
                  <Input
                    placeholder="City, State (e.g. Austin, TX)"
                    value={locationInput}
                    onChange={e => setLocationInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleGeocode()}
                    className="pl-9"
                  />
                </div>
                <Button onClick={handleGeocode} disabled={geocoding || !locationInput.trim()} variant="outline">
                  {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find'}
                </Button>
              </div>

              {location && (
                <div className={`p-3 rounded-lg text-sm ${
                  isAhp ? 'bg-green-50 text-green-800' : 'bg-green-500/10 text-green-400'
                }`}>
                  <Check className="w-4 h-4 inline mr-1.5" />
                  Location set: {location.label} ({location.lat.toFixed(4)}, {location.lon.toFixed(4)})
                </div>
              )}

              <div>
                <label className={`text-sm font-medium ${isAhp ? 'text-gray-700' : 'text-foreground'}`}>
                  Search Radius
                </label>
                <Select value={String(radiusMiles)} onValueChange={v => setRadiusMiles(Number(v))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25 miles</SelectItem>
                    <SelectItem value="50">50 miles</SelectItem>
                    <SelectItem value="100">100 miles</SelectItem>
                    <SelectItem value="150">150 miles</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Consolidation Slider */}
              <div>
                <label className={`text-sm font-medium ${isAhp ? 'text-gray-700' : 'text-foreground'}`}>
                  Hospital Consolidation
                </label>
                <div className="mt-3 px-1">
                  <Slider
                    value={[consolidationLevel]}
                    onValueChange={v => setConsolidationLevel(v[0])}
                    min={0}
                    max={100}
                    step={25}
                  />
                  <div className="flex justify-between mt-1.5">
                    <span className={`text-[10px] flex items-center gap-0.5 ${isAhp ? 'text-gray-400' : 'text-muted-foreground'}`}>
                      <DollarSign className="w-3 h-3" /> Lowest Price
                    </span>
                    <span className={`text-[10px] flex items-center gap-0.5 ${isAhp ? 'text-gray-400' : 'text-muted-foreground'}`}>
                      Fewest Hospitals <Building2 className="w-3 h-3" />
                    </span>
                  </div>
                </div>
                <p className={`text-xs mt-2 ${isAhp ? 'text-gray-500' : 'text-muted-foreground'}`}>
                  {{
                    0:   'Every procedure at the absolute cheapest hospital. May require visiting many hospitals per day.',
                    25:  'Split across hospitals, limited to 5 per day. Good savings with practical limits.',
                    50:  'Split only when savings are significant (15%+). Up to 3 hospitals per day.',
                    75:  'Same-day procedures grouped at one hospital. Split only for 40%+ savings.',
                    100: 'All same-day procedures at one hospital. Simplest logistics, potentially higher cost.',
                  }[consolidationLevel] || ''}
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {step === 3 && (
            <div className="space-y-4">
              <p className={`text-sm ${isAhp ? 'text-gray-600' : 'text-muted-foreground'}`}>
                Optional details for your care plan:
              </p>

              <div>
                <label className={`text-sm font-medium ${isAhp ? 'text-gray-700' : 'text-foreground'}`}>
                  Plan Name
                </label>
                <Input
                  value={planName}
                  onChange={e => setPlanName(e.target.value)}
                  placeholder={`Care Plan - ${location?.label || 'My Plan'}`}
                  className="mt-1"
                />
              </div>

              <div>
                <label className={`text-sm font-medium ${isAhp ? 'text-gray-700' : 'text-foreground'}`}>
                  Insurance Status
                </label>
                <Select value={insuranceStatus} onValueChange={setInsuranceStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uninsured">Uninsured</SelectItem>
                    <SelectItem value="insured">Insured</SelectItem>
                    <SelectItem value="medicare">Medicare</SelectItem>
                    <SelectItem value="medicaid">Medicaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 4: Review + Pay (checkout hidden in the app) */}
          {step === 4 && !showPayments && (
            <div className="space-y-4">
              <div className={`rounded-lg p-4 text-sm ${
                isAhp ? 'bg-gray-50 text-gray-500' : 'bg-foreground/5 text-muted-foreground'
              }`}>
                Care plan checkout isn't available in the app. Please visit our
                website to create a personalized care plan.
              </div>
            </div>
          )}
          {step === 4 && showPayments && (
            <div className="space-y-4">
              <p className={`text-sm ${isAhp ? 'text-gray-600' : 'text-muted-foreground'}`}>
                Review your care plan. Payment of $1.99 unlocks personalized optimization.
              </p>

              <div>
                <label className={`text-sm font-medium ${isAhp ? 'text-gray-700' : 'text-foreground'}`}>
                  Email (optional)
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1"
                />
                <p className={`text-xs mt-1 ${isAhp ? 'text-gray-400' : 'text-muted-foreground'}`}>
                  We'll email you a link to your optimized plan.
                </p>
              </div>

              <div className={`rounded-lg p-3 space-y-2 ${
                isAhp ? 'bg-gray-50' : 'bg-foreground/5'
              }`}>
                <h4 className={`text-sm font-semibold ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
                  Summary
                </h4>
                <div className={`text-sm space-y-1 ${isAhp ? 'text-gray-600' : 'text-muted-foreground'}`}>
                  <p><Stethoscope className="w-3.5 h-3.5 inline mr-1" />{selectedScenarios.length} scenarios selected</p>
                  <p><MapPin className="w-3.5 h-3.5 inline mr-1" />{location?.label} ({radiusMiles}mi radius)</p>
                  {planName && <p>Name: {planName}</p>}
                  <p>Insurance: {insuranceStatus}</p>
                  <p><Building2 className="w-3.5 h-3.5 inline mr-1" />
                    Consolidation: {
                      consolidationLevel <= 24 ? 'Max Savings' :
                      consolidationLevel <= 49 ? 'More Savings' :
                      consolidationLevel === 50 ? 'Balanced' :
                      consolidationLevel <= 74 ? 'More Convenient' :
                      'Most Convenient'
                    }
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selectedScenarios.map(s => (
                    <Badge key={s.id} variant="outline" className="text-xs">{s.name}</Badge>
                  ))}
                </div>
              </div>

              <div className={`flex items-center gap-2 text-xs ${isAhp ? 'text-gray-400' : 'text-muted-foreground'}`}>
                <Lock className="w-3.5 h-3.5" />
                Secure payment via Stripe. Use test card 4242 4242 4242 4242 in sandbox mode.
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-500 mt-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 flex justify-between border-t ${
          isAhp ? 'border-gray-100' : 'border-foreground/10'
        }`}>
          <Button
            variant="ghost"
            onClick={() => step === 1 ? onClose() : setStep(s => s - 1)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>

          {step < totalSteps ? (
            <Button onClick={() => setStep(s => s + 1)} disabled={!canNext}>
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : showPayments ? (
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Redirecting to Stripe...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-1" />
                  Pay $1.99 & Create Plan
                </>
              )}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
